import React, { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  CardGiftcard,
  CreditCard as CreditCardIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Order, ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS } from '../../../enums/order';
import { useCompany } from '../../../contexts/CompanyContext';
import { InvoiceService } from './invoiceService';

interface OrderTableProps {
  orders: Order[];
  onView: (order: Order) => void;
  onEdit: (order: Order) => void;
  onDelete: (order: Order) => void;
  deleteLoading: string;
  // ── NEW: passed from OrdersPage so getSoftChipSx() selects the correct
  // colour palette for the current theme mode.
  //
  // Without this prop every chip uses the light-mode palette (dark text on
  // pale backgrounds). In dark mode those colours — e.g. '#b76e00' on a
  // nearly-black paper — have very low contrast and are almost invisible.
  //
  // The parent derives this value with:
  //   const isDark = theme.palette.mode === 'dark';
  // and passes it here so both the page and the table always agree.
  isDark?: boolean;
  canEdit?: boolean;   // ADD
  canDelete?: boolean; // ADD
  canGenerateInvoice?: boolean; // ADD
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// Returns MUI `sx` props for a translucent, outlined chip.
//
// In light mode: soft pastel tints (low-opacity fills) + dark text.
// In dark mode:  richer semi-transparent fills + bright/light text so the chip
//                is clearly legible against dark paper backgrounds.
//
// Pass `isDark` (from `theme.palette.mode === 'dark'` in the parent) to switch
// between the two palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  // Light mode: subtle tints — text must be dark enough for WCAG contrast on
  // white/light-grey paper.
  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  // Dark mode: higher-opacity fills so the tint is visible on dark surfaces;
  // text shifted to light/vivid variants so chips pop without being garish.
  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon':  { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  onView,
  onEdit,
  onDelete,
  deleteLoading,
  isDark = false,
  canEdit = false,   // ADD
  canDelete = false, // ADD
  canGenerateInvoice = false, // ADD
}) => {
  const { companyProfile } = useCompany();
  const [downloadingInvoice, setDownloadingInvoice] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const dateA = a.orderDate ? new Date(a.orderDate).getTime() : 0;
      const dateB = b.orderDate ? new Date(b.orderDate).getTime() : 0;
      return dateB - dateA;
    });
  }, [orders]);

  const formatPrice = (price: number) => {
    const currency = companyProfile?.currency || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending:    'warning',
      confirmed:  'info',
      processing: 'primary',
      shipped:    'info',
      delivered:  'success',
      cancelled:  'error',
      returned:   'default',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    const icons: Record<string, React.ReactElement> = {
      pending:    <ShoppingCartIcon  fontSize="small" />,
      confirmed:  <CheckCircleIcon   fontSize="small" />,
      processing: <InventoryIcon     fontSize="small" />,
      shipped:    <LocalShippingIcon fontSize="small" />,
      delivered:  <CheckCircleIcon   fontSize="small" />,
      cancelled:  <CancelIcon        fontSize="small" />,
      returned:   <CancelIcon        fontSize="small" />,
    };
    return icons[status] ?? <ShoppingCartIcon fontSize="small" />;
  };

  const getPaymentStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending:            'warning',
      paid:               'success',
      failed:             'error',
      refunded:           'secondary',
      partially_refunded: 'secondary',
    };
    return colors[status] || 'default';
  };

  const getTotalItems = (items: any[]): number =>
    items.reduce((total, item) => total + item.quantity, 0);

  const handleDownloadInvoice = async (order: Order) => {
    if (!order.id) return;
    try {
      setDownloadingInvoice(order.id);
      await InvoiceService.generateInvoice(order, companyProfile);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice('');
    }
  };

  return (
    // ── height: '100%' is the key to full-height table behaviour ─────────────
    //
    // The parent Box in OrdersPage has:
    //   flex: 1  +  minHeight: { xs: 'auto', md: 0 }
    //
    // That means on md+ the Box stretches to fill all remaining vertical space
    // after the fixed header / pagination. Setting height:'100%' on this
    // TableContainer makes it fill that Box completely — so even with only
    // 1–2 rows the table occupies the full available area, matching the
    // ReturnsManagement pattern.
    //
    // Without height:'100%' the TableContainer wraps its content and the table
    // collapses to the height of its rows, leaving an ugly gap below.
    // ─────────────────────────────────────────────────────────────────────────
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: { xs: 500, md: 900 } },
      }}
    >
      <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell>Order Details</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Customer</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Items</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Total</TableCell>
            <TableCell>Status</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Payment</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Date</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedOrders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Box sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No orders found
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            sortedOrders.map((order) => (
              <TableRow
                key={order.id}
                hover
                sx={{
                  '&:hover': { backgroundColor: 'action.hover' },
                  '&:last-child td, &:last-child th': { borderBottom: 0 },
                }}
              >
                {/* ── Order Details ── */}
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {order.orderNumber}
                  </Typography>
                  {order.trackingNumber && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tracking: {order.trackingNumber}
                    </Typography>
                  )}
                  {order.allocatedWarehouseName && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                      <InventoryIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {order.allocatedWarehouseName}
                      </Typography>
                    </Stack>
                  )}
                  {order.stockReservationFailed && (
                    <Tooltip title={order.stockReservationError || 'Stock could not be automatically reserved for this order — needs manual warehouse allocation'}>
                      <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                        label="Stock issue"
                        size="small"
                        variant="outlined"
                        sx={{
                          ...getSoftChipSx('error', isDark),
                          height: 20, mt: 0.5,
                          '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                        }}
                      />
                    </Tooltip>
                  )}
                  {/* Mobile-only: customer name */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                  >
                    {order.customerName}
                  </Typography>
                  {/* Mobile-only: total */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'block', sm: 'none' } }}
                  >
                    {formatPrice(order.totalAmount)}
                  </Typography>
                </TableCell>

                {/* ── Customer ── */}
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200' }}>
                      <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {order.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {order.customerEmail}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* ── Items ── */}
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CardGiftcard sx={{ fontSize: 16, color: 'text.secondary', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2">
                        {getTotalItems(order.items)} item
                        {getTotalItems(order.items) !== 1 ? 's' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        from {order.items.length} product
                        {order.items.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                {/* ── Total ── */}
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" fontWeight="medium">
                    {formatPrice(order.totalAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Subtotal: {formatPrice(order.subtotal)}
                  </Typography>
                </TableCell>

                {/* ── Status chip — isDark switches to vivid-on-dark palette ── */}
                <TableCell>
                  <Chip
                    icon={getStatusIcon(order.status)}
                    label={ORDER_STATUS_LABELS[order.status]}
                    size="small"
                    variant="outlined"
                    sx={{
                      minWidth: { xs: 'auto', sm: 100 },
                      ...getSoftChipSx(getStatusColor(order.status), isDark),
                    }}
                  />
                </TableCell>

                {/* ── Payment chip — isDark switches to vivid-on-dark palette ── */}
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Chip
                      label={PAYMENT_STATUS_LABELS[order.paymentStatus]}
                      size="small"
                      variant="outlined"
                      sx={{
                        minWidth: 80,
                        ...getSoftChipSx(getPaymentStatusColor(order.paymentStatus), isDark),
                      }}
                    />
                  </Box>
                </TableCell>

                {/* ── Date ── */}
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(order.orderDate)}
                  </Typography>
                </TableCell>

                {/* ── Actions ── */}
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                    flexWrap="nowrap"
                  >
                    {canGenerateInvoice && (
                    <Tooltip title="Download Invoice">
                      <IconButton
                        onClick={() => handleDownloadInvoice(order)}
                        disabled={downloadingInvoice === order.id}
                        size="small"
                        color="success"
                      >
                        {downloadingInvoice === order.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <ReceiptIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    )}
                    <Tooltip title="View Order">
                      <IconButton onClick={() => onView(order)} size="small" color="primary">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEdit && (
                    <Tooltip title="Edit Order">
                      <IconButton onClick={() => onEdit(order)} size="small" color="warning">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    )}
                    {canDelete && (
                    <Tooltip title="Delete Order">
                      <IconButton
                        onClick={() => onDelete(order)}
                        disabled={deleteLoading === order.id}
                        size="small"
                        color="error"
                      >
                        {deleteLoading === order.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrderTable;